// --- React libraries ---
import React from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";

// Calculate if background is light or dark
function isLightBackground(hexColor?: string): boolean {
  if (!hexColor) return true; // Default to light
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155; // >155 = light background
}

export interface ChartWidgetConfig {
  title: string;
  chartType: "line" | "bar" | "column" | "pie" | "area";
  data: Array<{ label: string; [key: string]: string | number }>; // { label: "Jan", Sales: 100, Revenue: 1000 }
  series: string[]; // ["Sales", "Revenue", "Profit"] - column names
  seriesColors: Record<string, string>; // { "Sales": "#8b5cf6", "Revenue": "#3b82f6" }
  pieSelectedSeries?: string; // For pie charts: which series to display (e.g., "Sales")
  showLegend?: boolean; // Toggle legend visibility (default: true)
  backgroundColor?: string; // Widget background color
  csvInput?: string; // "Label,Sales,Revenue\nJanuary,100,1000\nFebruary,150,1500"
  xAxisLabel?: string;
  yAxisLabel?: string;
}

interface ChartWidgetProps {
  config: ChartWidgetConfig;
  width: number; // Grid columns (1-12)
  height: number; // Grid rows
}

// Format numbers for Y-axis (K, M, B)
function formatNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1000000000) return (value / 1000000000).toFixed(1) + "B";
  if (abs >= 1000000) return (value / 1000000).toFixed(1) + "M";
  if (abs >= 1000) return (value / 1000).toFixed(1) + "K";
  return value.toLocaleString();
}

export function ChartWidget({ config, width, height }: ChartWidgetProps) {
  const {
    title = "Untitled Chart",
    chartType = "line",
    data = [],
    series = [],
    seriesColors = {},
    pieSelectedSeries,
    showLegend = true,
    backgroundColor,
    xAxisLabel,
    yAxisLabel,
  } = config || {};

  // For backwards compatibility, if no series defined, try to extract from data
  const seriesKeys =
    series.length > 0
      ? series
      : data.length > 0
      ? Object.keys(data[0]).filter((k) => k !== "label")
      : [];

  // Default colors if not specified
  const defaultColors = ["#3b82f6", "#8b5cf6", "#10b981", "#ef4444", "#f97316"];
  const getSeriesColor = (seriesName: string, index: number) => {
    return (
      seriesColors[seriesName] || defaultColors[index % defaultColors.length]
    );
  };

  // Dynamic sizing based on widget dimensions
  const area = width * height;
  const isSmall = area <= 24; // 4×6 or smaller
  const isMedium = area <= 48; // 6×8 or smaller

  const titleSize = isSmall ? "text-sm" : isMedium ? "text-base" : "text-lg";
  const padding = isSmall ? "p-2" : isMedium ? "p-3" : "p-4";

  // Reactive text colors
  const isLight = isLightBackground(backgroundColor);
  const titleTextColor = backgroundColor
    ? isLight
      ? "text-gray-900"
      : "text-white"
    : "text-dashboard-primary";
  const noDataTextColor = backgroundColor
    ? isLight
      ? "text-gray-400"
      : "text-gray-300"
    : "text-dashboard-secondary";

  // No data state
  if (!data || data.length === 0) {
    return (
      <div
        className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} flex flex-col`}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <div className={`${titleSize} font-semibold ${titleTextColor} mb-2`}>
          {title}
        </div>
        <div
          className={`flex-1 flex items-center justify-center ${noDataTextColor} text-sm`}
        >
          No data available
        </div>
      </div>
    );
  }

  const renderChart = () => {
    switch (chartType) {
      case "line":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                label={
                  xAxisLabel
                    ? {
                        value: xAxisLabel,
                        position: "insideBottom",
                        offset: -10,
                      }
                    : undefined
                }
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <YAxis
                tickFormatter={formatNumber}
                label={
                  yAxisLabel
                    ? { value: yAxisLabel, angle: -90, position: "insideLeft" }
                    : undefined
                }
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <Tooltip
                formatter={(value: any) => value.toLocaleString()}
                contentStyle={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                }}
              />
              {showLegend && (
                <Legend
                  wrapperStyle={{
                    fontSize: "12px",
                    paddingTop: xAxisLabel ? "15px" : "5px",
                  }}
                />
              )}
              {seriesKeys.map((seriesKey, index) => (
                <Line
                  key={seriesKey}
                  type="monotone"
                  dataKey={seriesKey}
                  stroke={getSeriesColor(seriesKey, index)}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  name={seriesKey}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case "column":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                label={
                  xAxisLabel
                    ? {
                        value: xAxisLabel,
                        position: "insideBottom",
                        offset: -10,
                      }
                    : undefined
                }
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <YAxis
                tickFormatter={formatNumber}
                label={
                  yAxisLabel
                    ? { value: yAxisLabel, angle: -90, position: "insideLeft" }
                    : undefined
                }
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <Tooltip
                formatter={(value: any) => value.toLocaleString()}
                contentStyle={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                }}
              />
              {showLegend && (
                <Legend
                  wrapperStyle={{
                    fontSize: "12px",
                    paddingTop: xAxisLabel ? "15px" : "5px",
                  }}
                />
              )}
              {seriesKeys.map((seriesKey, index) => (
                <Bar
                  key={seriesKey}
                  dataKey={seriesKey}
                  fill={getSeriesColor(seriesKey, index)}
                  radius={[4, 4, 0, 0]}
                  name={seriesKey}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case "bar":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              layout="vertical"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                type="number"
                tickFormatter={formatNumber}
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
                width={100}
              />
              <Tooltip
                formatter={(value: any) => value.toLocaleString()}
                contentStyle={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                }}
              />
              {showLegend && <Legend wrapperStyle={{ fontSize: "12px" }} />}
              {seriesKeys.map((seriesKey, index) => (
                <Bar
                  key={seriesKey}
                  dataKey={seriesKey}
                  fill={getSeriesColor(seriesKey, index)}
                  radius={[0, 4, 4, 0]}
                  name={seriesKey}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case "pie": {
        // For pie charts, use selected series or first series
        const pieSeriesKey = pieSelectedSeries || seriesKeys[0] || "value";

        // Filter out zero values to hide 0% slices
        const pieData = data.filter((d) => Number(d[pieSeriesKey]) > 0);
        const total = pieData.reduce(
          (sum, d) => sum + (Number(d[pieSeriesKey]) || 0),
          0
        );
        void total; // Total is used for reference

        return (
          <div className="relative w-full h-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey={pieSeriesKey}
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={isSmall ? 40 : isMedium ? 60 : 80}
                  label={(entry) => {
                    const percent =
                      ((Number(entry[pieSeriesKey]) || 0) / total) * 100;
                    return percent > 0
                      ? `${entry.label}: ${percent.toFixed(1)}%`
                      : "";
                  }}
                  labelLine={{ stroke: "#6b7280" }}
                >
                  {pieData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={defaultColors[index % defaultColors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any) => value.toLocaleString()}
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                  }}
                />
                {showLegend && <Legend wrapperStyle={{ fontSize: "12px" }} />}
              </PieChart>
            </ResponsiveContainer>
          </div>
        );
      }

      case "area":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <defs>
                {seriesKeys.map((seriesKey, index) => {
                  const color = getSeriesColor(seriesKey, index);
                  return (
                    <linearGradient
                      key={`gradient-${seriesKey}`}
                      id={`colorGradient-${seriesKey}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                label={
                  xAxisLabel
                    ? {
                        value: xAxisLabel,
                        position: "insideBottom",
                        offset: -10,
                      }
                    : undefined
                }
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <YAxis
                tickFormatter={formatNumber}
                label={
                  yAxisLabel
                    ? { value: yAxisLabel, angle: -90, position: "insideLeft" }
                    : undefined
                }
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <Tooltip
                formatter={(value: any) => value.toLocaleString()}
                contentStyle={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                }}
              />
              {showLegend && (
                <Legend
                  wrapperStyle={{
                    fontSize: "12px",
                    paddingTop: xAxisLabel ? "15px" : "5px",
                  }}
                />
              )}
              {seriesKeys.map((seriesKey, index) => (
                <Area
                  key={seriesKey}
                  type="monotone"
                  dataKey={seriesKey}
                  stroke={getSeriesColor(seriesKey, index)}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill={`url(#colorGradient-${seriesKey})`}
                  name={seriesKey}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} flex flex-col overflow-hidden`}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <div className={`${titleSize} font-semibold ${titleTextColor} mb-2`}>
        {title}
      </div>
      <div className="flex-1 min-h-0">{renderChart()}</div>
    </div>
  );
}
